import styled from "styled-components";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { ArrowRight } from "@phosphor-icons/react";

import { updateProfile } from "../../store/actions";

import Step1 from "./Steps/Step1";
import Step2 from "./Steps/Step2";
import Step3 from "./Steps/Step3";
import Step4 from "./Steps/Step4";
import Step5 from "./Steps/Step5";
import Step6 from "./Steps/Step6";

import { validate } from "../Shared/inputValidator";
import Modal from "../Shared/Modal";
import { Button, labelStyles } from "../Shared/ui";

const STEP_TITLES = [
  "Basic details",
  "About you",
  "Education and work",
  "Address",
  "Family and beliefs",
  "Partner preferences",
];

const Registration = ({ user, profile, updateProfile }) => {
  const step = profile?.step ?? '';
  const navigate = useNavigate();
  const payloadRef = useRef({});

  // Only send people to their account when they finish the last step here,
  // not every time a fully registered member opens the site.
  const prevStepRef = useRef(step);
  useEffect(() => {
    const prev = prevStepRef.current;
    prevStepRef.current = step;
    if (step === 6 && typeof prev === 'number' && prev < 6) {
      navigate('/account');
    }
  }, [step, navigate]);

  const setPayload = (key, value) => {
    payloadRef.current[key] = value;
  };

  const nextClick = () => {
    const fieldsToValidate = [];
    const stepContainer = document.getElementById("step");

    if (stepContainer) {
      stepContainer.childNodes.forEach(childNode => {
        if (childNode?.attributes?.['data-error-type']) {
          fieldsToValidate.push(childNode.name);
        }
      });
    }

    if (validate(fieldsToValidate)) {
      const currentStep = profile?.step ?? 0;
      updateProfile(user, profile, {
        ...payloadRef.current,
        step: currentStep + 1,
      });
    }
  };

  if (step === '' || step >= 6) return null;

  const stepNumber = Math.min(Number(step) + 1, STEP_TITLES.length);

  return (
    <Modal labelledBy="registration-title" width={560}>
      <Header>
        <span>Step {stepNumber} of {STEP_TITLES.length}</span>
        <h2 id="registration-title">{STEP_TITLES[stepNumber - 1] || "Begin your success story"}</h2>
        <Progress aria-hidden="true">
          <span style={{ transform: `scaleX(${stepNumber / STEP_TITLES.length})` }} />
        </Progress>
      </Header>
      <Message>Fields marked * are mandatory</Message>
      <Body key={step}>
        <StepRenderer step={step} payload={payloadRef.current} setPayload={setPayload} />
      </Body>
      <Footer>
        <Button onClick={nextClick}>
          {stepNumber === STEP_TITLES.length ? "Finish" : "Next"} <ArrowRight size={18} weight="bold" />
        </Button>
      </Footer>
    </Modal>
  );
};

const StepRenderer = ({ step, payload, setPayload }) => {
  switch (step) {
    case 0: return <Step1 payload={payload} setPayload={setPayload} />;
    case 1: return <Step2 payload={payload} setPayload={setPayload} />;
    case 2: return <Step3 payload={payload} setPayload={setPayload} />;
    case 3: return <Step4 payload={payload} setPayload={setPayload} />;
    case 4: return <Step5 payload={payload} setPayload={setPayload} />;
    case 5: return <Step6 payload={payload} setPayload={setPayload} />;
    default: return null;
  }
};

const Header = styled.div`
  margin-bottom: 8px;

  > span {
    font-size: 14px;
    font-weight: 600;
    color: var(--coral);
  }

  h2 {
    font-size: 28px;
    margin: 4px 0 16px;
  }
`;

const Progress = styled.div`
  height: 6px;
  border-radius: 6px;
  background: var(--surface-tint);
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    background: var(--coral);
    border-radius: inherit;
    transform-origin: left;
    transition: transform 0.7s var(--ease-out);
  }
`;

const Message = styled.p`
  font-size: 14px;
  color: var(--text-muted);
  margin: 0 0 20px;
`;

const Body = styled.div`
  animation: step-in 0.5s var(--ease-out) both;

  #step > label,
  #step > span:not(.error-message) {
    ${labelStyles}
  }

  #step > .error-message {
    margin-top: -10px;
    margin-bottom: 14px;
  }

  @keyframes step-in {
    from { opacity: 0; transform: translateX(16px); }
    to { opacity: 1; transform: none; }
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
  position: sticky;
  bottom: 0;
`;

// Redux connection
const mapStateToProps = (state) => ({
  user: state.userState.user,
  profile: state.profileState.profile,
});

const mapDispatchToProps = (dispatch) => ({
  updateProfile: (user, profile, payload) => dispatch(updateProfile(user, profile, payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Registration);
